import { useEffect, useRef, useState } from 'react'
import { Gift, X } from 'lucide-react'
import FortuneWheelModal from './FortuneWheelModal'
import FortuneFab from './FortuneFab'
import { localDayKey } from './fabGeometry'
import { fortuneIntroDecision } from '../../utils/fortuneGate'
import { useT } from '../../hooks/useTranslation'
import { useDashboardStore } from '../../store/dashboardStore'

// Розыгрыш бонусов: маленькое окно при входе + плавающая кнопка колеса.
//
// Окно показывается один раз в календарный день юзера (локальная дата
// устройства). День записывается в localStorage сразу при показе — повторный
// запуск приложения в тот же день окно не вернёт. Кнопка у края видна всегда,
// пока функция включена: через неё колесо можно открыть в любой момент.
// Включается и выключается в админке («Система» и раздел «Колесо фортуны»),
// флаги приходят в публичном appConfig.
//
// Новичку окно НЕ показываем (задача владельца 2026-10-21): пока юзера ждёт
// пробный бот, приглашение колеса не должно пересекаться с приглашением создать
// бота. Решение принимает utils/fortuneGate.js по флагам ready/suppressIntro;
// кнопка колеса у края при этом остаётся на месте.

function currentUserId() {
  try {
    return window.Telegram?.WebApp?.initDataUnsafe?.user?.id || 0
  } catch {
    return 0
  }
}

// Запасной вариант, если localStorage недоступен (приватный режим): окно
// покажется один раз за жизнь страницы, а не на каждый рендер.
const seenInMemory = new Map()

function gateKey(userId) {
  return `scalpex-fortune-gate-${userId || 'anon'}`
}

function hasSeenGate(userId, day) {
  const key = gateKey(userId)
  if (seenInMemory.get(key) === day) return true
  try {
    return localStorage.getItem(key) === day
  } catch {
    return false
  }
}

function markGateSeen(userId, day) {
  const key = gateKey(userId)
  seenInMemory.set(key, day)
  try {
    localStorage.setItem(key, day)
  } catch { /* приватный режим — остаётся память страницы */ }
}

// ready         — профиль и список ботов уже пришли с сервера (иначе окно
//                 решало бы по «пустому» списку на первом кадре);
// suppressIntro — юзера ждёт пробный бот: окно молчит, день помечается.
export default function FortuneGate({ ready = true, suppressIntro = false }) {
  const t = useT()
  const appConfig = useDashboardStore(s => s.appConfig)
  const [visible, setVisible] = useState(false)
  const [open, setOpen] = useState(false)
  const checkedRef = useRef(false)

  const fw = appConfig?.fortuneWheel
  const enabled = !!fw?.enabled
  const introEnabled = !!fw?.introEnabled

  useEffect(() => {
    if (checkedRef.current) return
    const userId = currentUserId()
    const day = localDayKey()
    const decision = fortuneIntroDecision({
      enabled,
      introEnabled,
      ready,
      suppressIntro,
      seenToday: hasSeenGate(userId, day),
    })
    // 'off' — функция выключена; 'wait' — данные ещё грузятся. В обоих случаях
    // день НЕ помечаем: иначе окно пропало бы на весь день из-за первой секунды
    // загрузки. Эффект перезапустится, когда ready/suppressIntro изменятся.
    if (decision === 'off' || decision === 'wait') return
    // Дальше решение окончательное для этой сессии: повторный рендер не должен
    // показывать окно снова.
    checkedRef.current = true
    if (decision === 'seen') return
    // 'suppress' — новичок ждёт пробного бота: не показываем, но день
    // записываем, иначе окно выскочит сегодня же сразу после создания бота.
    if (decision === 'suppress') { markGateSeen(userId, day); return }
    markGateSeen(userId, day)
    setVisible(true)
  }, [enabled, introEnabled, ready, suppressIntro])

  if (!enabled) return null

  if (open) {
    return <FortuneWheelModal onClose={() => setOpen(false)} />
  }

  return (
    <>
      {!visible && <FortuneFab onClick={() => setOpen(true)} label={t.fortuneTitle} />}

      {visible && (
        <div className="fortune-gate" role="dialog" aria-modal="true" aria-label={t.fortuneIntroTitle}>
          <div className="fortune-gate-card">
            <button
              className="fortune-gate-close"
              onClick={() => setVisible(false)}
              aria-label={t.fortuneClose}
            >
              <X size={13} strokeWidth={2.5} />
            </button>

            <div className="fortune-gate-icon">
              <Gift size={24} strokeWidth={1.9} />
            </div>

            <div className="fortune-gate-title">{t.fortuneIntroTitle}</div>
            <div className="fortune-gate-text">{t.fortuneIntroText}</div>

            <button
              className="fortune-btn fortune-btn-primary"
              onClick={() => { setVisible(false); setOpen(true) }}
            >
              {t.fortuneIntroTake}
            </button>
            <div className="fortune-gate-hint">{t.fortuneIntroHint}</div>
          </div>
        </div>
      )}
    </>
  )
}
